/**
 * Pillar dot-field search animation, for the "Intelligent search and
 * assistance" metric card.
 *
 * The section renders the field as a static CSS radial-gradient. This draws the
 * same grid on a canvas over it and plays a search across it:
 *
 *   - a query point appears on a dot and a pulse ripples out from it, swelling
 *     and darkening the dots it passes
 *   - the pulse dies away before the far edges -- the search covers the content
 *     it is allowed to reach, not the whole field -- and a few dots inside that
 *     reach light up as results, each with a faint line back to the query
 *   - pointing at the field sends a pulse out from the pointer
 *
 * The CSS dots only hide once the canvas has drawn, so without JavaScript, and
 * with prefers-reduced-motion, the static field is all anyone sees. The loop
 * stops while the card is off screen.
 *
 * The grid copies the section's background-size (13px) and dot radius (1.6px),
 * and lays the dots out the way background-repeat:space does, so nothing
 * shifts at the moment the canvas takes over.
 */
(function () {
  'use strict';

  var TILE = 13;
  var DOT = 1.6;
  var CYCLE = 4200;     // ms from one automatic search to the next
  var SPEED = 0.12;     // px per ms the pulse travels
  var BAND = 9;         // px, the width of the pulse's crest
  var HOLD = 1400;      // ms the results stay lit once the pulse has passed
  var FADE = 600;       // ms they take to go out
  var RESULTS = 6;
  var POINTER_GAP = 900;

  // Dot centres along one side. background-repeat:space draws only whole
  // tiles and shares the spare length out between them; a lone tile centres.
  function axis(len) {
    var n = Math.max(1, Math.floor(len / TILE));
    var gap = n > 1 ? (len - n * TILE) / (n - 1) : 0;
    var start = n > 1 ? 0 : (len - TILE) / 2;
    var out = [];
    for (var i = 0; i < n; i++) out.push(start + i * (TILE + gap) + TILE / 2);
    return out;
  }

  function rgb(value, fallback) {
    var m = /^#([0-9a-f]{6})$/i.exec((value || '').trim());
    if (!m) return fallback;
    var n = parseInt(m[1], 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }

  function mix(a, b, t, alpha) {
    return 'rgba(' +
      Math.round(a[0] + (b[0] - a[0]) * t) + ',' +
      Math.round(a[1] + (b[1] - a[1]) * t) + ',' +
      Math.round(a[2] + (b[2] - a[2]) * t) + ',' +
      (alpha == null ? 1 : alpha) + ')';
  }

  function smooth(e0, e1, x) {
    var t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0)));
    return t * t * (3 - 2 * t);
  }

  function init(field) {
    if (!field || field.dataset.dotsBound === '1') return;
    field.dataset.dotsBound = '1';

    var canvas = document.createElement('canvas');
    canvas.setAttribute('aria-hidden', 'true');
    canvas.style.cssText = 'position:absolute;top:0;left:0;width:100%;height:100%;display:block;pointer-events:none';
    var ctx = canvas.getContext('2d');
    if (!ctx) return;

    // --brand is var(--blue-600); a custom property's computed value has its
    // var() already substituted, so both come back as plain hex.
    var css = getComputedStyle(field);
    var base = rgb(css.getPropertyValue('--blue-300'), [156, 187, 221]);
    var brand = rgb(css.getPropertyValue('--brand'), [53, 105, 169]);

    var w = 0;
    var h = 0;
    var dots = [];      // flat x, y pairs
    var pulses = [];
    var nextAuto = 0;
    var lastPulse = -Infinity;
    var raf = null;
    var visible = !('IntersectionObserver' in window);
    var live = false;
    var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');

    function measure() {
      w = field.clientWidth;
      h = field.clientHeight;
      var dpr = Math.min(2, window.devicePixelRatio || 1);
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      var xs = axis(w);
      var ys = axis(h);
      dots = [];
      for (var j = 0; j < ys.length; j++) {
        for (var i = 0; i < xs.length; i++) dots.push(xs[i], ys[j]);
      }
      // Results are indexes into dots, so a new grid drops them.
      pulses = [];
    }

    function life(p) {
      return p.reach / SPEED + HOLD + FADE;
    }

    function pulse(x, y, now) {
      var reach = 0.42 * Math.sqrt(w * w + h * h);
      var pool = [];
      for (var k = 0; k < dots.length; k += 2) {
        var d = Math.sqrt((dots[k] - x) * (dots[k] - x) + (dots[k + 1] - y) * (dots[k + 1] - y));
        if (d > reach * 0.25 && d < reach * 0.85) pool.push(k);
      }
      var res = [];
      for (var n = 0; n < RESULTS && pool.length; n++) {
        res.push(pool.splice(Math.floor(Math.random() * pool.length), 1)[0]);
      }
      pulses.push({ x: x, y: y, t0: now, reach: reach, res: res });
      lastPulse = now;
    }

    // Automatic searches start on a dot in the middle 60% of the field, so the
    // pulse has room to spread before it fades.
    function autoPulse(now) {
      var pool = [];
      for (var k = 0; k < dots.length; k += 2) {
        if (dots[k] > w * 0.2 && dots[k] < w * 0.8 && dots[k + 1] > h * 0.2 && dots[k + 1] < h * 0.8) pool.push(k);
      }
      if (!pool.length) return;
      var k2 = pool[Math.floor(Math.random() * pool.length)];
      pulse(dots[k2], dots[k2 + 1], now);
    }

    function draw(now) {
      ctx.clearRect(0, 0, w, h);
      pulses = pulses.filter(function (p) { return now - p.t0 < life(p); });

      // The field. Dots the pulse is not touching go in one path; the rest
      // are drawn after it, each at its own size and colour.
      var touched = [];
      ctx.fillStyle = mix(base, base, 0);
      ctx.beginPath();
      for (var k = 0; k < dots.length; k += 2) {
        var x = dots[k];
        var y = dots[k + 1];
        var s = 0;
        for (var p = 0; p < pulses.length; p++) {
          var P = pulses[p];
          var d = Math.sqrt((x - P.x) * (x - P.x) + (y - P.y) * (y - P.y));
          var off = d - (now - P.t0) * SPEED;
          s += Math.exp(-(off * off) / (2 * BAND * BAND)) * (1 - smooth(P.reach * 0.55, P.reach, d));
        }
        if (s > 0.02) {
          touched.push(k, Math.min(1, s));
        } else {
          ctx.moveTo(x + DOT, y);
          ctx.arc(x, y, DOT, 0, 2 * Math.PI);
        }
      }
      ctx.fill();
      for (var t = 0; t < touched.length; t += 2) {
        var st = touched[t + 1];
        ctx.fillStyle = mix(base, brand, st * 0.85);
        ctx.beginPath();
        ctx.arc(dots[touched[t]], dots[touched[t] + 1], DOT + st * 1.4, 0, 2 * Math.PI);
        ctx.fill();
      }

      // Each search's results, then its query point on top. A result lights
      // as the pulse reaches it, and they all go out together.
      for (var q = 0; q < pulses.length; q++) {
        var Q = pulses[q];
        var age = now - Q.t0;
        var out = 1 - smooth(Q.reach / SPEED + HOLD, life(Q), age);

        ctx.lineWidth = 1;
        for (var r = 0; r < Q.res.length; r++) {
          var rx = dots[Q.res[r]];
          var ry = dots[Q.res[r] + 1];
          var at = Math.sqrt((rx - Q.x) * (rx - Q.x) + (ry - Q.y) * (ry - Q.y)) / SPEED;
          var g = smooth(at, at + 220, age) * out;
          if (g <= 0) continue;
          ctx.strokeStyle = mix(brand, brand, 0, 0.22 * g);
          ctx.beginPath();
          ctx.moveTo(Q.x, Q.y);
          ctx.lineTo(rx, ry);
          ctx.stroke();
          ctx.fillStyle = mix(brand, brand, 0, 0.14 * g);
          ctx.beginPath();
          ctx.arc(rx, ry, DOT + 5 * g, 0, 2 * Math.PI);
          ctx.fill();
          ctx.fillStyle = mix(base, brand, g);
          ctx.beginPath();
          ctx.arc(rx, ry, DOT + 1.4 * g, 0, 2 * Math.PI);
          ctx.fill();
        }

        var qa = smooth(0, 200, age) * out;
        if (qa <= 0) continue;
        ctx.fillStyle = mix(brand, brand, 0, qa);
        ctx.beginPath();
        ctx.arc(Q.x, Q.y, 3.2, 0, 2 * Math.PI);
        ctx.fill();
        ctx.strokeStyle = mix(brand, brand, 0, 0.35 * qa);
        ctx.beginPath();
        ctx.arc(Q.x, Q.y, 6.5, 0, 2 * Math.PI);
        ctx.stroke();
      }
    }

    function frame(now) {
      raf = null;
      if (!live) return;
      if (now >= nextAuto) {
        autoPulse(now);
        nextAuto = now + CYCLE;
      }
      draw(now);
      if (visible) raf = requestAnimationFrame(frame);
    }

    function wake() {
      if (live && visible && !raf) raf = requestAnimationFrame(frame);
    }

    function start() {
      if (live) return;
      field.appendChild(canvas);
      measure();
      live = true;
      nextAuto = performance.now() + 500;
      draw(performance.now());
      field.setAttribute('data-live', '');
      wake();
    }

    function stop() {
      live = false;
      if (raf) cancelAnimationFrame(raf);
      raf = null;
      pulses = [];
      field.removeAttribute('data-live');
      if (canvas.parentNode) canvas.parentNode.removeChild(canvas);
    }

    // Live query, so switching the OS setting takes effect without a reload.
    function sync() {
      if (reduceMotion && reduceMotion.matches) stop();
      else start();
    }

    field.addEventListener('pointermove', function (e) {
      if (!live) return;
      var now = performance.now();
      if (now - lastPulse < POINTER_GAP) return;
      var box = field.getBoundingClientRect();
      pulse(e.clientX - box.left, e.clientY - box.top, now);
      nextAuto = now + CYCLE;
      wake();
    });

    if ('ResizeObserver' in window) {
      new ResizeObserver(function () {
        if (!live) return;
        measure();
        draw(performance.now());
      }).observe(field);
    }

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        visible = entries[0].isIntersecting;
        wake();
      }, { rootMargin: '120px' }).observe(field);
    }

    if (reduceMotion) {
      if (reduceMotion.addEventListener) reduceMotion.addEventListener('change', sync);
      else if (reduceMotion.addListener) reduceMotion.addListener(sync);
    }
    sync();
  }

  function boot() {
    var fields = document.querySelectorAll('[data-metric-dots]');
    for (var i = 0; i < fields.length; i++) init(fields[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();
